import { createMemo, createSignal, For, Show } from "solid-js";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Loader2,
  RefreshCw,
  Scan,
  Type,
} from "lucide-solid";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import {
  type AdminFontRow,
  cn,
  type components,
  formatBytes,
  getAdminFontAuditHandlers,
  getVariantHealthSummary,
} from "@listto/shared";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";

type AuditResult = components["schemas"]["FontAuditResult"];
type AuditAllResult = components["schemas"]["FontAuditAllResult"];

const SKELETON_COUNT = 5;

export function FontAudit() {
  const { t } = useI18n();
  const [singleAudit, setSingleAudit] = createSignal<AuditResult | null>(null);
  const [allAudit, setAllAudit] = createSignal<AuditAllResult | null>(null);
  const [isAuditing, setIsAuditing] = createSignal(false);
  const [auditError, setAuditError] = createSignal<Error | null>(null);

  const fontsQuery = createApiQuery<AdminFontRow[], components["schemas"]["ErrorModel"]>(
    () => ["admin", "fonts"],
    async () => api.GET("/api/admin/font/listAll", {}),
    () => ({ staleTime: 30_000 }),
  );

  const reIngestMutation = createApiMutation(async (vars: { fontId: string; weight: string }) =>
    api.POST("/api/admin/font/reIngestVariant", { body: vars }),
  );

  const handlers = createMemo(() =>
    getAdminFontAuditHandlers({
      actions: {
        auditSingle: async (fontId) => {
          const { data, error } = await api.POST("/api/admin/font/audit", { body: { fontId } });
          return { data: data ?? null, error };
        },
        auditAll: async () => {
          const { data, error } = await api.GET("/api/admin/font/auditAll", {});
          return { data: data ?? null, error };
        },
      },
      setSingleAudit,
      setAllAudit,
      setIsAuditing,
      onError: (err) => setAuditError(err),
    }),
  );

  return (
    <div class="space-y-8">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Type class="h-8 w-8 text-primary" />
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Font Audit`)}</h2>
        </div>
        <Button
          variant="default"
          onClick={() => void handlers().runAllAudit()}
          disabled={isAuditing()}
        >
          <Show when={isAuditing()} fallback={<Scan class="mr-2 h-4 w-4" />}>
            <Loader2 class="mr-2 h-4 w-4 animate-spin" />
          </Show>
          {t(msg`Audit All`)}
        </Button>
      </div>

      <Show when={auditError()}>
        <Card class="border-destructive bg-destructive/5">
          <CardContent class="p-4 flex items-center gap-3 text-destructive">
            <AlertTriangle class="h-5 w-5" />
            <p class="text-sm font-medium">{auditError()?.message}</p>
          </CardContent>
        </Card>
      </Show>

      <div class="grid gap-6 lg:grid-cols-4">
        <aside class="lg:col-span-1 space-y-4">
          <h3 class="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            {t(msg`Fonts`)}
          </h3>
          <div class="flex flex-col gap-1 max-h-[600px] overflow-y-auto pr-2">
            <For
              each={fontsQuery.data}
              fallback={
                <div class="space-y-2">
                  <For each={Array.from({ length: SKELETON_COUNT })}>
                    {() => <Skeleton class="h-9 w-full" />}
                  </For>
                </div>
              }
            >
              {(font) => (
                <Button
                  variant={singleAudit()?.fontId === font.id ? "secondary" : "ghost"}
                  class="justify-between group px-3"
                  onClick={() => void handlers().runSingleAudit(font.id)}
                  disabled={isAuditing()}
                >
                  <span class="truncate">{font.family}</span>
                  <ChevronRight class="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Button>
              )}
            </For>
          </div>
        </aside>

        <main class="lg:col-span-3 space-y-6">
          <Show
            when={singleAudit()}
            fallback={
              <Show when={allAudit()}>
                {(audit) => (
                  <Card>
                    <CardHeader>
                      <CardTitle>{t(msg`Audit Summary`)}</CardTitle>
                      <p class="text-sm text-muted-foreground">
                        {t(msg`{{total}} fonts audited • {{withIssues}} with issues`, {
                          total: audit().total,
                          withIssues: audit().withIssues,
                        })}
                      </p>
                    </CardHeader>
                    <CardContent>
                      <div class="space-y-3">
                        <For each={audit().fonts}>
                          {(f) => (
                            <div class="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                              <div>
                                <p class="font-bold">{f.family}</p>
                                <p class="text-xs text-muted-foreground">
                                  {t(msg`{{count}} variants • {{status}}`, {
                                    count: f.variantCount,
                                    status: f.isActive ? t(msg`Active`) : t(msg`Inactive`),
                                  })}
                                </p>
                              </div>
                              <div class="flex items-center gap-3">
                                <Show
                                  when={f.hasIssues}
                                  fallback={<CheckCircle2 class="h-5 w-5 text-success" />}
                                >
                                  <AlertTriangle class="h-5 w-5 text-amber-500" />
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => void handlers().runSingleAudit(f.fontId)}
                                  >
                                    {t(msg`Details`)}
                                  </Button>
                                </Show>
                              </div>
                            </div>
                          )}
                        </For>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </Show>
            }
          >
            {(audit) => (
              <Card>
                <CardHeader class="flex-row items-center justify-between space-y-0">
                  <div>
                    <CardTitle class="text-2xl">{audit().family}</CardTitle>
                    <p class="text-xs text-muted-foreground mt-1">
                      {t(msg`Audited on {{date}}`, {
                        date: new Date(audit().auditedAt).toLocaleString(),
                      })}
                    </p>
                  </div>
                  <Badge variant={audit().hasIssues ? "destructive" : "outline"}>
                    {audit().hasIssues ? t(msg`With issues`) : t(msg`Healthy`)}
                  </Badge>
                </CardHeader>
                <CardContent class="space-y-6">
                  <For each={audit().variants}>
                    {(variant) => (
                      <div class="space-y-3 rounded-xl border bg-muted/10 p-4">
                        <div class="flex items-center justify-between">
                          <h4 class="font-bold">
                            {t(msg`Weight {{weight}}`, { weight: variant.weight })}
                          </h4>
                          <Show when={variant.hasIssues}>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                reIngestMutation.mutate(
                                  {
                                    fontId: audit().fontId,
                                    weight: variant.weight,
                                  },
                                  {
                                    onSuccess: () => {
                                      void handlers().runSingleAudit(audit().fontId);
                                    },
                                  },
                                );
                              }}
                              disabled={reIngestMutation.isPending}
                            >
                              <RefreshCw
                                class={cn(
                                  "mr-2 h-3 w-3",
                                  reIngestMutation.isPending && "animate-spin",
                                )}
                              />
                              {t(msg`Re-ingest`)}
                            </Button>
                          </Show>
                        </div>
                        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <AuditMetric
                            label="TTF"
                            ok={variant.ttf.exists}
                            sub={
                              variant.ttf.exists ? formatBytes(variant.ttf.size) : t(msg`Not found`)
                            }
                          />
                          <AuditMetric
                            label="WOFF2"
                            ok={variant.woff2.exists}
                            sub={
                              variant.woff2.exists
                                ? formatBytes(variant.woff2.size)
                                : t(msg`Not found`)
                            }
                          />
                          <AuditMetric
                            label={t(msg`Attachment`)}
                            ok={variant.attachment.ok}
                            sub={variant.attachment.linked ? t(msg`Linked`) : t(msg`Unlinked`)}
                          />
                        </div>
                        <Show when={variant.hasIssues}>
                          <p class="text-[10px] text-amber-600 bg-amber-500/5 p-2 rounded border border-amber-200">
                            <strong>{t(msg`Diagnosis`)}:</strong>{" "}
                            {getVariantHealthSummary(variant.ttf)}
                          </p>
                        </Show>
                      </div>
                    )}
                  </For>
                </CardContent>
              </Card>
            )}
          </Show>
        </main>
      </div>
    </div>
  );
}

function AuditMetric(props: { label: string; ok: boolean; subText?: string; sub?: string }) {
  return (
    <div class="flex items-center gap-2">
      <div class={cn("h-2 w-2 rounded-full", props.ok ? "bg-success" : "bg-destructive")} />
      <div class="flex flex-col">
        <span class="text-xs font-semibold">{props.label}</span>
        <span class="text-[10px] text-muted-foreground">{props.sub}</span>
      </div>
    </div>
  );
}
